import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiUpload, FiCamera, FiType, FiX } from 'react-icons/fi';
import { TbQrcode } from 'react-icons/tb';
import './QRScan.css';
import { auth, db } from '../../firebase/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import jsQR from 'jsqr';

export default function QRScan() {
  const navigate = useNavigate();
  const [scanMethod, setScanMethod] = useState('webcam'); // 'webcam', 'upload', 'manual'
  const [qrData, setQrData] = useState('');               // raw scanned string (kept internally)
  const [recipientId, setRecipientId] = useState(null);   // parsed Firebase UID
  const [recipientInfo, setRecipientInfo] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userWinicoins, setUserWinicoins] = useState(0);
  const [amount, setAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);

  // Scanner loop refs
  const rafRef = useRef(null);
  const detectingRef = useRef(false);
  const detectorRef = useRef(null);
  const hasBarcodeDetector = typeof window !== 'undefined' && 'BarcodeDetector' in window;
  const detectionCooldownMs = 600; // avoid multi-fires
  const lastHitRef = useRef(0);

  // Fetch user's winicoins
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const walletRef = doc(db, 'users', user.uid, 'wallet', 'winicoins');
          const walletDoc = await getDoc(walletRef);
          if (walletDoc.exists()) setUserWinicoins(walletDoc.data().winicoins || 0);
        } catch (e) {
          console.error('Error fetching winicoins:', e);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  // Check camera availability once
  useEffect(() => {
    const checkCameraAvailability = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(d => d.kind === 'videoinput');
        if (videoDevices.length === 0) {
          setError('No camera detected on this device');
          setScanMethod('upload');
        }
      } catch (err) {
        console.error('Error checking camera availability:', err);
      }
    };
    checkCameraAvailability();
  }, []);

  // Ensure camera is stopped when leaving webcam tab or unmounting
  useEffect(() => {
    if (scanMethod !== 'webcam') stopWebcam();
    return () => stopWebcam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanMethod]);

  const startWebcam = async () => {
    try {
      setError('');
      stopWebcam();

      // Create detector if supported
      if (hasBarcodeDetector && !detectorRef.current) {
        try {
          detectorRef.current = new window.BarcodeDetector({ formats: ['qr_code'] });
        } catch (e) {
          console.warn('BarcodeDetector init failed, will fallback to jsQR:', e);
          detectorRef.current = null;
        }
      }

      const constraints = {
        video: {
          facingMode: 'user', // change to 'environment' for rear camera
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;

        const playVideo = async () => {
          try {
            await videoRef.current.play();
            setIsScanning(true);
            setSuccess('');
            startScanLoop(); // begin decoding frames
          } catch (e) {
            console.error('Video play error:', e);
            setError('Could not start video. Tap “Start Camera” again or try Upload.');
            setIsScanning(false);
          }
        };

        if (videoRef.current.readyState >= 2) {
          playVideo();
        } else {
          videoRef.current.onloadedmetadata = playVideo;
        }

        videoRef.current.onerror = (e) => {
          console.error('Video error:', e);
          setError('Video playback error');
          setIsScanning(false);
          stopWebcam();
        };
      }
    } catch (err) {
      console.error('Webcam error:', err);
      setError('Camera access denied. Please try file upload instead.');
      setIsScanning(false);
      setScanMethod('upload');
    }
  };

  const stopWebcam = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    detectingRef.current = false;

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      try { videoRef.current.pause(); } catch {}
      videoRef.current.srcObject = null;
    }
    setIsScanning(false);
  };

  const startScanLoop = () => {
    if (!videoRef.current) return;
    detectingRef.current = true;

    const loop = async () => {
      if (!detectingRef.current) return;

      try {
        const video = videoRef.current;
        const canvas = canvasRef.current;

        if (video.readyState >= 2 && canvas) {
          // Take centered square from the frame (matches your square overlay)
          const vw = video.videoWidth;
          const vh = video.videoHeight;
          const size = Math.min(vw, vh);
          const sx = (vw - size) / 2;
          const sy = (vh - size) / 2;

          canvas.width = size;
          canvas.height = size;

          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size);

          const now = performance.now();
          if (now - lastHitRef.current > detectionCooldownMs) {
            const hit = await tryDetectQRCode(canvas);
            if (hit) {
              lastHitRef.current = now;
              onDetected(hit);
              return; // stop the loop after a successful detection
            }
          }
        }
      } catch (e) {
        // keep loop alive
      }

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
  };

  const tryDetectQRCode = async (canvas) => {
    // 1) BarcodeDetector if available
    if (detectorRef.current) {
      try {
        const barcodes = await detectorRef.current.detect(canvas);
        if (barcodes && barcodes.length) {
          const first = barcodes.find(b => b.format === 'qr_code') || barcodes[0];
          if (first?.rawValue) return first.rawValue;
        }
      } catch (e) {
        console.warn('BarcodeDetector detect failed, fallback to jsQR:', e);
        detectorRef.current = null;
      }
    }

    // 2) Fallback: jsQR
    try {
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const result = jsQR(img.data, img.width, img.height, { inversionAttempts: 'attemptBoth' });
      if (result && result.data) return result.data;
    } catch (e) {}

    return null;
  };

 // Accepts: 
//  - wini:uid:<UID>
//  - wini:<UID>
//  - wini:user_<UID>
//  - uid:<UID>
//  - raw <UID>
//  - any of the above with extra suffix like ":<timestamp>"
//  - URL-like payloads (wini://..., https://... ?uid=<UID> or .../<UID>)
const validateQRData = (data) => {
  const s = decodeURIComponent(String(data || '').trim());

  // URL-like formats
  try {
    if (/^(https?:|wini:\/\/)/i.test(s)) {
      const urlish = s.replace(/^wini:\/\//i, 'https://'); // coerce custom scheme
      const u = new URL(urlish);
      const byParam = u.searchParams.get('uid');
      if (byParam) return { isValid: true, recipientId: byParam };
      const last = u.pathname.split('/').filter(Boolean).pop();
      if (last && /^[A-Za-z0-9_-]{6,128}$/.test(last)) {
        return { isValid: true, recipientId: last };
      }
    }
  } catch {}

  // Tokenized formats
  const UID = '([A-Za-z0-9_-]{6,128})'; // Firebase UIDs can vary; don't be too strict
  const patterns = [
    new RegExp(`^wini:uid:${UID}`, 'i'),
    new RegExp(`^wini:user_${UID}`, 'i'),
    new RegExp(`^wini:${UID}`, 'i'),
    new RegExp(`^uid:${UID}`, 'i'),
    new RegExp(`^${UID}$`, 'i'),
  ];
  for (const re of patterns) {
    const m = re.exec(s);
    if (m) return { isValid: true, recipientId: m[1] };
  }

  // Fallback: pick the first colon-separated token that looks like a UID
  const candidate = s.split(':').find(t => /^[A-Za-z0-9_-]{6,128}$/.test(t));
  if (candidate) return { isValid: true, recipientId: candidate };

  return { isValid: false, message: 'Invalid QR format. Expected e.g. "wini:uid:<FIREBASE_UID>".' };
};


const onDetected = (data) => {
  // ...existing stop logic...
  setQrData(data);

  const parsed = validateQRData(data);
  if (parsed.isValid) {
    setRecipientInfo(null);                 // show "Fetching..." immediately
   setRecipientId(parsed.recipientId);     // <-- important: drive the UI state
   fetchRecipientInfo(parsed.recipientId); // async fetch
    setSuccess('QR code detected!');
    setError('');
  } else {
    setError(parsed.message || 'Invalid QR code');
    setSuccess('');
  }
};


  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file (PNG, JPG, JPEG)');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('File size too large. Please select an image under 5MB.');
        return;
      }
      setIsUploading(true);
      setError('');

      const reader = new FileReader();
      reader.onload = () => {
        setTimeout(async () => {
          try {
            const imgEl = new Image();
            imgEl.onload = () => {
              const canvas = canvasRef.current;
              const ctx = canvas.getContext('2d', { willReadFrequently: true });
              const size = Math.min(imgEl.width, imgEl.height);
              const sx = (imgEl.width - size) / 2;
              const sy = (imgEl.height - size) / 2;
              canvas.width = size;
              canvas.height = size;
              ctx.drawImage(imgEl, sx, sy, size, size, 0, 0, size, size);
              const imageData = ctx.getImageData(0, 0, size, size);
              const result = jsQR(imageData.data, size, size);
              if (result?.data) {
                const parsed = validateQRData(result.data);
                if (parsed.isValid) {
                  setQrData(result.data);
                  setRecipientInfo(null);
                  setRecipientId(parsed.recipientId);
                  setRecipientInfo(null);
                  fetchRecipientInfo(parsed.recipientId);
                  setSuccess('QR code detected successfully!');
                  setError('');
                } else {
                  setError(parsed.message);
                }
              } else {
                setError('No QR code found in the image.');
              }
              setIsUploading(false);
              if (fileInputRef.current) fileInputRef.current.value = '';
            };
            imgEl.onerror = () => {
              setIsUploading(false);
              setError('Failed to read the image.');
            };
            imgEl.src = reader.result;
          } catch (e) {
            setIsUploading(false);
            setError('Could not process the image.');
          }
        }, 150);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleManualInput = () => {
    if (!qrData.trim()) {
      setError('Please enter valid QR code data');
      return;
    }
    const parsed = validateQRData(qrData);
    if (parsed.isValid) {
      setRecipientInfo(null);
      setRecipientId(parsed.recipientId);
      fetchRecipientInfo(parsed.recipientId);
      setSuccess('QR data entered successfully!');
      setError('');
    } else {
      setError(parsed.message);
    }
  };

  const handleMethodChange = (method) => {
    if (method !== 'webcam') stopWebcam();
    setScanMethod(method);
  };

  const fetchRecipientInfo = async (uid) => {
    try {
      const userRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) return null;

      
      const userData = userDoc.data();
      const info = {
        id: uid,
        username: userData.name || userData.username || (userData.email ? userData.email.split('@')[0] : 'Unknown User'),
        profileImage: userData.profileImage || '/default-avatar.png'
      };
      setRecipientInfo(info);
      return info;
    } catch (err) {
      console.error('Error fetching recipient info:', err);
      setError('Error fetching recipient info');
      return null;
    }
  };

  const handleSend = async () => {
    if (!recipientId) {
      setError('No recipient selected. Scan a valid QR first.');
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (parseFloat(amount) > userWinicoins) {
      setError('Insufficient winicoins');
      return;
    }

    setIsProcessing(true);
    setError('');

    // Ensure recipient still exists
    const recipient = recipientInfo || (await fetchRecipientInfo(recipientId));
    if (!recipient) {
      setIsProcessing(false);
      return;
    }

    // TODO: implement actual transfer logic here
    setSuccess(`Successfully sent ${amount} winicoins to ${recipient.username}`);
    setAmount('');
    setIsProcessing(false);
    setTimeout(() => setSuccess(''), 3000);
  };

  const resetForm = () => {
    setQrData('');
    setRecipientId(null);
    setRecipientInfo(null);
    setAmount('');
    setError('');
    setSuccess('');
    setScanMethod('webcam');
    stopWebcam();
  };

  return (
    <div className="qr-scan-page">
      {/* Header */}
      <div className="qr-header">
        <button className="qr-back" onClick={() => navigate(-1)} aria-label="Back">
          <FiArrowLeft />
        </button>
        <h1 className="qr-title">Send via QR Code</h1>
        <div className="qr-header-spacer" />
      </div>

      <div className="qr-content">
        {/* Method Selection */}
        <div className="method-selector">
          <button
            className={`method-btn ${scanMethod === 'webcam' ? 'active' : ''}`}
            onClick={() => handleMethodChange('webcam')}
          >
            <FiCamera />
            <span>Camera</span>
          </button>
          <button
            className={`method-btn ${scanMethod === 'upload' ? 'active' : ''}`}
            onClick={() => handleMethodChange('upload')}
          >
            <FiUpload />
            <span>Upload</span>
          </button>
          <button
            className={`method-btn ${scanMethod === 'manual' ? 'active' : ''}`}
            onClick={() => handleMethodChange('manual')}
          >
            <FiType />
            <span>Manual</span>
          </button>
        </div>

        {/* Webcam Scanner */}
        {scanMethod === 'webcam' && (
          <div className="scanner-section">
            <div className="scanner-container">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="scanner-video"
                style={{ display: isScanning ? 'block' : 'none', objectFit: 'cover' }}
              />
              {!isScanning && (
                <div className="scanner-overlay">
                  <TbQrcode className="scanner-icon" />
                  <p>Click to start camera</p>
                  <button
                    className="start-camera-btn"
                    onClick={startWebcam}
                    disabled={isScanning}
                  >
                    {isScanning ? 'Starting...' : 'Start Camera'}
                  </button>
                </div>
              )}
              {isScanning && (
                <>
                  <div className="scanner-frame">
                    <div className="scanner-corner top-left"></div>
                    <div className="scanner-corner top-right"></div>
                    <div className="scanner-corner bottom-left"></div>
                    <div className="scanner-corner bottom-right"></div>
                  </div>
                  <div className="scanning-indicator">
                    <div className="scanning-dots">
                      <span></span><span></span><span></span>
                    </div>
                    <p>Scanning for QR codes...</p>
                  </div>
                </>
              )}
            </div>

            {/* Hidden canvas for QR code processing */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />

            <div className="camera-controls">
              <button className="stop-camera-btn" onClick={stopWebcam} disabled={!isScanning}>
                Stop Camera
              </button>
            </div>
          </div>
        )}

        {/* File Upload */}
        {scanMethod === 'upload' && (
          <div className="upload-section">
            <div
              className="upload-area"
              onClick={() => !isUploading && fileInputRef.current?.click()}
            >
              {isUploading ? (
                <div className="upload-loading">
                  <div className="spinner"></div>
                  <p>Processing QR code...</p>
                </div>
              ) : (
                <>
                  <FiUpload className="upload-icon" />
                  <p>Click to upload QR code image</p>
                  <span>Supports: PNG, JPG, JPEG (max 5MB)</span>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </div>
        )}

        {/* Manual Input */}
        {scanMethod === 'manual' && (
          <div className="manual-section">
            <div className="input-group">
              <label htmlFor="qr-input">QR Code Data:</label>
              <textarea
                id="qr-input"
                value={qrData}
                onChange={(e) => setQrData(e.target.value)}
                placeholder='Enter e.g. "wini:uid:<FIREBASE_UID>" or just the UID'
                rows={3}
              />
            </div>
            <button
              className="validate-btn"
              onClick={handleManualInput}
              disabled={!qrData.trim()}
            >
              Validate QR Data
            </button>
          </div>
        )}

        {/* Scanned user display */}
        {(recipientId || recipientInfo) && (
          <div className="qr-data-display">
            <h3>Scanned User</h3>
            <div className="scanned-user-info">
              <div className="qr-circular-avatar">
                {recipientInfo ? (
                  <img
                    src={recipientInfo.profileImage}
                    alt={recipientInfo.username}
                    className="recipient-avatar"
                    onError={(e) => { e.currentTarget.src = '/default-avatar.png'; }}
                  />
                ) : (
                  <div className="recipient-avatar-placeholder"><span>?</span></div>
                )}
              </div>
              <div className="recipient-details">
                <span className="recipient-name">
                  {recipientInfo ? recipientInfo.username : 'Fetching user info...'}
                </span>
              </div>
              <button
                className="clear-btn"
                onClick={resetForm}
                aria-label="Clear scanned user"
              >
                <FiX />
              </button>
            </div>

            {/* (Optional) show raw string for debugging; remove if you don't want it */}
            {qrData && (
              <div className="qr-data-content">
                <code>{qrData}</code>
              </div>
            )}
          </div>
        )}

        {/* Transfer Form */}
        {recipientInfo && (
          <div className="transfer-form">
            <h3>Send Winicoins</h3>

            <div className="input-group">
              <label htmlFor="amount-input">Amount (Winicoins):</label>
              <input
                id="amount-input"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount..."
                min="0.01"
                max={userWinicoins}
                step="0.01"
              />
              <span className="balance-info">
                Available: {userWinicoins.toFixed(2)} winicoins
              </span>
            </div>

            <button
              className="send-btn"
              onClick={handleSend}
              disabled={isProcessing || !amount || parseFloat(amount) <= 0 || parseFloat(amount) > userWinicoins}
            >
              {isProcessing ? 'Processing...' : 'Send Winicoins'}
            </button>
          </div>
        )}

        {/* Error/Success Messages */}
        {error && (
          <div className="message error">
            <span>{error}</span>
            <button onClick={() => setError('')} className="close-msg" aria-label="Close error">
              <FiX />
            </button>
          </div>
        )}
        {success && (
          <div className="message success">
            <span>{success}</span>
            <button onClick={() => setSuccess('')} className="close-msg" aria-label="Close success">
              <FiX />
            </button>
          </div>
        )}

        {/* Reset Button */}
        {(recipientInfo || qrData) && (
          <button className="reset-btn" onClick={resetForm}>
            Start Over
          </button>
        )}
      </div>
    </div>
  );
}
